// client/src/components/pages/TelegramBI/Analytics/UserActivityChart.js
import React, { useState, useEffect } from 'react';
import { FaDatabase, FaClock, FaTrophy } from 'react-icons/fa';

const UserActivityChart = ({ data, loading }) => {
  const [mounted, setMounted] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 150);
    return () => clearTimeout(timer);
  }, []);

  // Reset animation on data change
  useEffect(() => {
    setMounted(false);
    const timer = setTimeout(() => setMounted(true), 150);
    return () => clearTimeout(timer);
  }, [data]);

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-gray-200 p-5">
        <div className="animate-pulse space-y-3">
          <div className="h-5 bg-gray-200 rounded w-40" />
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-10 bg-gray-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  const queries = Array.isArray(data) ? data : [];

  if (queries.length === 0) {
    return (
      <div className="bg-surface rounded-lg border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FaTrophy className="text-amber-500" />
            Top Queries
          </h3>
        </div>
        <div className="px-5 py-12 text-center">
          <FaDatabase className="mx-auto text-3xl text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">No query activity data available</p>
          <p className="text-xs text-gray-400 mt-1">
            Top queries will appear here once they have been executed
          </p>
        </div>
      </div>
    );
  }

  const maxExecutionCount = Math.max(...queries.map((q) => q.execution_count || 0), 1);

  // Rank badge colors
  const getRankStyle = (index) => {
    if (index === 0) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (index === 1) return 'bg-gray-100 text-gray-600 border-gray-200';
    if (index === 2) return 'bg-orange-50 text-orange-600 border-orange-200';
    return 'bg-gray-50 text-gray-500 border-gray-100';
  };

  // Bar color gradient based on rank
  const getBarColor = (index) => {
    if (index === 0) return 'bg-primary-500';
    if (index === 1) return 'bg-primary-400';
    if (index === 2) return 'bg-primary-300';
    return 'bg-gray-300';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '--';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);

    if (days > 7) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (mins > 0) return `${mins}m ago`;
    return 'Just now';
  };

  return (
    <div className="bg-surface rounded-lg border border-gray-200">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <FaTrophy className="text-amber-500" />
          Top Queries
        </h3>
        <span className="text-[11px] text-gray-400">{queries.length} queries</span>
      </div>
      <div className="p-3">
        <div className="space-y-1">
          {queries.map((query, index) => {
            const barWidth = mounted
              ? Math.max(((query.execution_count || 0) / maxExecutionCount) * 100, 3)
              : 0;
            const isHovered = hoveredIndex === index;

            return (
              <div
                key={query.query_id || index}
                className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-default ${
                  isHovered ? 'bg-gray-50' : ''
                }`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Rank */}
                <div
                  className={`w-6 h-6 rounded-md border flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${getRankStyle(index)}`}
                >
                  {index + 1}
                </div>

                {/* Name and bar */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-gray-900 truncate mr-3">
                      {query.query_name || `Query ${query.query_id}`}
                    </span>
                    <span className="text-sm font-semibold text-gray-900 flex-shrink-0 tabular-nums">
                      {(query.execution_count || 0).toLocaleString()}
                    </span>
                  </div>

                  {/* Bar */}
                  <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`absolute top-0 left-0 h-full rounded-full transition-all duration-700 ease-out ${getBarColor(index)}`}
                      style={{
                        width: `${barWidth}%`,
                        transitionDelay: `${index * 60}ms`,
                      }}
                    />
                  </div>

                  {/* Meta row */}
                  <div className="flex items-center gap-3 mt-1.5">
                    {query.avg_execution_time != null && (
                      <span className="flex items-center gap-1 text-[11px] text-gray-400">
                        <FaClock className="text-[9px]" />
                        {typeof query.avg_execution_time === 'number'
                          ? `${query.avg_execution_time.toFixed(0)}ms avg`
                          : `${query.avg_execution_time}ms avg`}
                      </span>
                    )}
                    {query.last_executed && (
                      <span className="text-[11px] text-gray-400">
                        Last run: {formatTime(query.last_executed)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UserActivityChart;
